import { makeStyles } from 'tss-react/mui';
import { Timesheet, TimesheetDay } from 'api/report/types';
import { TimesheetSettingsType } from 'api/customer/types';
import { TimesheetReportTemplate } from 'utils/report';
import { Summary } from 'report/logic/timesheet/calculations';
import NormalTimesheet from './NormalTimesheet';
import StartStopTimesheet from './StartStopTimesheet';
import { ProfessionIdentifier } from 'api/supplier/types';
import Alert, { ALERT_STATUS } from 'components/interface/alert/Alert';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import TextArea from 'components/interface/textField/TextArea';
import Typography from '@mui/material/Typography';
import { BinarySwitch } from 'components/interface/Switch';

const useStyles = makeStyles()(theme => ({
    base: {
        marginBottom: theme.spacing(4),
    },
    toolbar: {
        marginBottom: theme.spacing(1),
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'baseline',
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        columnGap: theme.spacing(2),
    },
    comment: {
        marginBottom: theme.spacing(4),
    },
}));

export interface TimesheetProps {
    timesheetSetting: TimesheetSettingsType;
    timesheet: Timesheet;
    totalHours: string;
    template: TimesheetReportTemplate;
    summary: Summary;
    canSubmit: boolean;
    disabled: boolean;
    loading: boolean;
    enableSickHours: boolean;
    onEnableSickHours: (value: boolean) => void;
    onUpdateTimesheetDay: (
        updateFunc: (day: TimesheetDay) => TimesheetDay,
        index: number
    ) => void;
    onNotesFieldChange: (value: string) => void;
    useSplitTextfields: boolean;

    commentContainsSickHints: boolean;
    sickHoursDuplicateDays: TimesheetDay[];
}

export default function TimesheetComponent({
    timesheetSetting,
    timesheet,
    totalHours,
    template,
    canSubmit,
    disabled,
    loading,
    enableSickHours,
    onEnableSickHours,
    onNotesFieldChange,
    useSplitTextfields,

    commentContainsSickHints,
    sickHoursDuplicateDays,
    ...rest
}: TimesheetProps): JSX.Element {
    const { classes } = useStyles();

    const isSSK = template.professions.some(
        profession => profession.identifier === ProfessionIdentifier.NURSE
    );
    const allowSickHours = isSSK && canSubmit;

    const renderComment = () => {
        return (
            <div className={classes.comment}>
                <TextArea
                    id='notesTimesheet'
                    label='Kommentar'
                    value={timesheet?.notes || ''}
                    onChange={onNotesFieldChange}
                    disabled={!canSubmit || disabled}
                    fullWidth
                />
            </div>
        );
    };

    return (
        <div className={classes.base}>
            <div className={classes.toolbar}>
                <div
                    className={classes.actions}
                    onClickCapture={event => event.preventDefault()}>
                    {allowSickHours && (
                        <BinarySwitch
                            label='Rapporera sjuktimmar'
                            value={enableSickHours || false}
                            onChange={v => onEnableSickHours(!!v)}
                            disabled={!canSubmit || disabled}
                            asText
                            asToggleButton={false}
                        />
                    )}
                </div>
            </div>
            {sickHoursDuplicateDays.length > 0 && (
                <Alert
                    status={ALERT_STATUS.WARNING}
                    text={
                        <Typography variant='body2' color='inherit'>
                            Det verkar som att du har rapporterat sjuktimmar som
                            är lika med dina normala timmar. Om du har varit
                            sjuk behöver du inte rapportera sjuktimmarna under
                            arbetade timmar. Påverkade dagar:
                            {sickHoursDuplicateDays.map((day, i) => (
                                <i key={i}>
                                    <br />
                                    <b>
                                        {format(new Date(day.date), 'EEEE', {
                                            locale: sv,
                                        })}
                                    </b>
                                </i>
                            ))}
                        </Typography>
                    }
                    style={{ marginBottom: 16 }}
                    fullWidth
                />
            )}
            {timesheetSetting === TimesheetSettingsType.START_STOP_TIME ? (
                <StartStopTimesheet
                    {...rest}
                    timesheet={timesheet}
                    totalHours={totalHours}
                    template={template.data}
                    canSubmit={canSubmit}
                    disabled={disabled}
                    loading={loading}
                    enableSickHours={enableSickHours}
                />
            ) : (
                <NormalTimesheet
                    {...rest}
                    days={timesheet.days ?? []}
                    template={template.data}
                    totalHours={totalHours}
                    canSubmit={canSubmit}
                    disabled={disabled}
                    loading={loading}
                    enableSickHours={enableSickHours}
                    useSplitTextfields={useSplitTextfields}
                />
            )}
            {commentContainsSickHints && allowSickHours && !enableSickHours && (
                <Alert
                    status={ALERT_STATUS.WARNING}
                    text='Det verkar som att du har skrivit något om sjukdom i din kommentar. Om du har varit sjuk under veckan, glöm inte att rapportera sjuktimmar. Det gör du genom att klicka på "Ja" vid "Rapportera sjuktimmar" ovanför tabellen.'
                    style={{ marginBottom: 16 }}
                    fullWidth
                />
            )}
            {template.data.hasComment ? renderComment() : undefined}
        </div>
    );
}
